"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

type MeResponse =
  | { loggedIn: false }
  | {
      loggedIn: true;
      user: {
        id: string;
        email: string;
        naam: string;
      };
    };

export default function LoginPage() {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fout, setFout] = useState("");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [laden, setLaden] = useState(true);

  useEffect(() => {
    async function loadMe() {
      try {
        const res = await fetch("/api/me", {
          cache: "no-store",
        });

        const data: MeResponse = await res.json();
        setMe(data);

        if (data.loggedIn) {
          router.replace("/");
          return;
        }
      } catch (error) {
        console.error("Fout bij ophalen loginstatus:", error);
      } finally {
        setLaden(false);
      }
    }

    loadMe();
  }, [router]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFout("");

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setFout(data.error || "Login mislukt.");
        return;
      }

      router.replace("/");
      router.refresh();
    } catch (error) {
      console.error("Login fout:", error);
      setFout("Er ging iets mis bij het inloggen.");
    }
  }

  async function handleLogout() {
    try {
      await fetch("/api/logout", { method: "POST" });

      setMe({ loggedIn: false });
      setEmail("");
      setPassword("");
      setFout("");
      router.refresh();
    } catch (error) {
      console.error("Logout fout:", error);
    }
  }

  if (laden) {
    return (
      <main className="relative min-h-screen overflow-hidden flex items-center justify-center px-6">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/bgm.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

        <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/20 bg-white/10 p-8 text-white shadow-2xl backdrop-blur-xl">
          <p className="text-center text-lg font-medium animate-pulse">Laden...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden flex items-center justify-center px-6">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      >
        <source src="/bgm.mp4" type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl">
        <div className="mb-8 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Inloggen</h1>
              <p className="mt-2 text-sm text-white/80">
                Welkom terug. Log in om verder te gaan.
              </p>
            </div>

            {me?.loggedIn && (
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm text-white transition hover:bg-white/20"
              >
                Uitloggen
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              E-mailadres
            </label>
            <input
              type="email"
              placeholder="jij@sushipoint.nl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 outline-none transition focus:border-white/50 focus:bg-white/15"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              Wachtwoord
            </label>
            <input
              type="password"
              placeholder="Voer je wachtwoord in"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 outline-none transition focus:border-white/50 focus:bg-white/15"
            />
          </div>

          {fout ? (
            <div className="rounded-2xl border border-red-400/30 bg-red-500/15 px-4 py-3 text-sm text-red-200">
              {fout}
            </div>
          ) : null}

          <button
            type="submit"
            className="w-full rounded-2xl bg-white px-4 py-3 font-semibold text-black transition hover:scale-[1.01] hover:bg-white/90 active:scale-[0.99]"
          >
            Login
          </button>
        </form>
      </div>
    </main>
  );
}